import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, DatePicker, Input, InputNumber, message, Modal, Select, Switch, Table } from 'antd';
import dayjs from 'dayjs';
import BaseForm from '../../../../components/form/BaseForm';
import SearchBox from '../../../../components/outline/SearchBox';
import Button from '../../../../components/Button';
import {
  EligibleItem,
  PreviewRequest,
  PreviewResponse,
} from '../../../../lib/api/settlementDateAdjustment';
import { settlementDateAdjustmentQuery } from '../../queries';
import {
  useExecuteMutation,
  usePreviewMutation,
} from '../../hooks/useAdjustmentMutations';
import { useDueDateOverrideMap } from '../../hooks/useDueDateOverrideMap';
import { useEligibleItemsFilter } from '../../hooks/useEligibleItemsFilter';
import { useShiftConfig } from '../../hooks/useShiftConfig';
import { useEligibleItemsPreview } from '../../../settlement/hooks/useEligibleItemsPreview';
import {
  SETTLEMENT_ITEM_PERIOD_TYPE_LABEL,
  SETTLEMENT_ITEM_STATUS_LABEL,
} from '../../constants/labels';
import { ExcelUploadModal } from '../ExcelUploadModal/ExcelUploadModal';
import { ItemHistoryModal } from '../ItemHistoryModal/ItemHistoryModal';
import { PreviewModal } from '../PreviewModal/PreviewModal';
import { ShiftForm } from '../ShiftForm/ShiftForm';
import { useEligibleItemsColumns } from './columns';
import {
  StyledActionCard,
  StyledButtonGroup,
  StyledContainer,
  StyledCountText,
  StyledHistoryOnlySwitch,
  StyledToolbar,
} from './styles';

type Props = {
  onExecuteSuccess?: () => void;
};

/**
 * 탭1: 변경 작업 실행.
 *
 * 레이아웃은 기존 정산 리스트 페이지(SettlementListSearchBox + Toolbar + Table)
 * 흐름과 맞춰서 — 검색박스 → 변경 조건 설정 카드 → 선택 건수 툴바 → 테이블.
 * 이동 조건 폼을 테이블 위에 배치해 사용자가 스크롤하지 않고도 미리보기까지
 * 한 흐름으로 작업할 수 있게 한다.
 *
 * State 그룹별로 훅으로 분리:
 * - useEligibleItemsFilter: 검색 필터 + 정산번호 raw 입력
 * - useShiftConfig: 변경 조건(mode/deltaDays/targetBaseDate/reason) + 사전 검증 + payload 빌더
 * - useDueDateOverrideMap: PreviewModal 의 행별 dueDate 수동 지정
 */
export const ExecuteTabPanel = ({ onExecuteSuccess }: Props): React.JSX.Element => {
  // ─── 필터 ────────────────────────────────────────────
  const filter = useEligibleItemsFilter();

  // ─── 변경 조건 ────────────────────────────────────────
  const shiftConfig = useShiftConfig();

  // ─── 선택 ──────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // 운영자가 PreviewModal 에서 행별로 직접 지정한 dueDate.
  // Preview 새로 받을 때/모달 닫을 때/Execute 성공 시 reset 호출.
  const { overrideMap, setOverride, resetOverrides, toPayload } =
    useDueDateOverrideMap();

  // ─── 모달 state ──────────────────────────────────────
  const [excelModalOpen, setExcelModalOpen] = useState(false);
  const [historyItemId, setHistoryItemId] = useState<number | null>(null);

  // ─── 데이터 ──────────────────────────────────────────
  // 페이지 진입 시 자동 fetch 금지 — eligibleItems 는 10+ 테이블 join 으로 비싼 쿼리.
  // 운영자가 검색 조건을 지정하고 [검색] 을 눌렀을 때만 발동 (`filter.isApplied`).
  const eligibleQuery = useQuery({
    ...settlementDateAdjustmentQuery.eligibleItems(filter.appliedFilter),
    enabled: filter.isApplied,
  });
  const previewMutation = usePreviewMutation();
  const executeMutation = useExecuteMutation();

  // ─── Preview 흐름 — 기준일/완료일 변경 공유 훅 ───────
  // 새 preview 가 오면 이전 수동 지정값들은 무효 — onAfterPreviewSuccess 슬롯으로 reset.
  const {
    previewResult,
    setPreviewResult,
    currentSource,
    runPreview,
  } = useEligibleItemsPreview<PreviewRequest['shift'], PreviewResponse>({
    buildShift: shiftConfig.buildShift,
    onSubmit: ({ ids, shift }) =>
      previewMutation.mutateAsync({
        selection: { mode: 'idList', ids },
        shift,
      }),
    onAfterPreviewSuccess: resetOverrides,
  });

  const items: EligibleItem[] = eligibleQuery.data?.items ?? [];
  const totalCount = eligibleQuery.data?.totalCount ?? 0;

  // ─── 테이블 컬럼 ────────────────────────────────────
  const columns = useEligibleItemsColumns(setHistoryItemId);

  // ─── 핸들러 ──────────────────────────────────────────
  const handleSearch = (): void => {
    filter.applyFilters();
    setSelectedIds([]);
  };

  const handleResetFilter = (): void => {
    filter.reset();
    setSelectedIds([]);
  };

  /**
   * Preview API 호출. idsOverride 가 주어지면 테이블 체크 상태 대신 그 id들을 사용 (엑셀 업로드 경로).
   * 공유 훅이 검증/호출/state 갱신/override reset 까지 처리하고, 엑셀 업로드 시
   * 체크 상태 동기화만 여기서 처리.
   */
  const handlePreview = async (idsOverride?: number[]): Promise<void> => {
    const ok = await runPreview(selectedIds, idsOverride);
    // idsOverride 로 들어온 경우(엑셀 업로드) 테이블 체크 상태도 동기화해
    // preview 취소 시 사용자가 업로드한 대상이 어떤 건지 표에서 확인 가능.
    if (ok && idsOverride) {
      setSelectedIds(idsOverride);
    }
  };

  const handleExecute = (): void => {
    // Preview 통과 후 도달하므로 정상 케이스에선 항상 통과 — 방어적으로 한 번 더 검증.
    const shiftResult = shiftConfig.buildShift();
    if ('error' in shiftResult) {
      message.warning(shiftResult.error);
      return;
    }

    // 실제 실행 건수는 Preview 응답의 `items.length` — 서버가 completed/extraDelivery/returnDelivery/notFound 를 제외한 뒤의 적용 대상.
    // selectedIds.length 는 테이블 체크박스 + 엑셀 업로드 포함한 "요청한 수" 라 제외 건수가 함께 집계돼 실제 변경 수와 불일치.
    const applyCount = previewResult?.items.length ?? selectedIds.length;
    const excludedCount = previewResult?.summary.excludedCount ?? 0;
    const overrideCount = overrideMap.size;
    const excludedSuffix =
      excludedCount > 0 ? ` (제외 ${excludedCount}건은 반영되지 않습니다)` : '';
    const overrideSuffix =
      overrideCount > 0 ? ` 정산 예정일을 직접 지정한 ${overrideCount}건이 포함됩니다.` : '';

    Modal.confirm({
      title: '정산 기준일 변경을 실행합니다',
      content: `${applyCount}건을 변경합니다. 이 작업은 변경 이력에 기록됩니다.${excludedSuffix}${overrideSuffix}`,
      okText: '실행',
      okButtonProps: { danger: true },
      cancelText: '취소',
      onOk: async () => {
        try {
          const res = await executeMutation.mutateAsync({
            selection: { mode: 'idList', ids: selectedIds },
            shift: shiftResult.shift,
            reason: shiftConfig.reason,
            source: currentSource,
            dueDateOverrides: toPayload(),
          });
          message.success(`완료. 배치 ${res.batchId} (${res.appliedCount}건)`);
          setPreviewResult(null);
          resetOverrides();
          setSelectedIds([]);
          shiftConfig.resetReason();
          onExecuteSuccess?.();
        } catch (e) {
          message.error(
            `실행 실패: ${e instanceof Error ? e.message : String(e)}`,
          );
        }
      },
    });
  };

  /**
   * 엑셀 업로드 모달에서 파싱 완료 후 "미리보기" 버튼 → 즉시 Preview 호출.
   * 다른 관리자 페이지의 엑셀 업로드 플로우처럼 중간 체크 단계를 생략한다.
   */
  const handleExcelUploadSubmit = (ids: number[]): void => {
    setExcelModalOpen(false);
    void handlePreview(ids);
  };

  return (
    <StyledContainer>
      <Alert
        type="warning"
        showIcon
        message="이 기능은 정산아이템의 기준일/예정일을 일괄 이동합니다. 실행 내역은 변경 이력에 남습니다."
      />

      {/* ─── 검색 박스 ──────────────────────────────── */}
      <SearchBox onSearch={handleSearch} onClear={handleResetFilter}>
        <BaseForm
          label="정산번호"
          content={
            <Input
              style={{ width: 320 }}
              placeholder="예: 535355, 535447 (콤마/공백으로 여러 개 가능)"
              value={filter.idsInputRaw}
              onChange={(e) => filter.setIdsInputRaw(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          }
        />
        <BaseForm
          label="정산 기준일"
          content={
            <DatePicker.RangePicker
              // 초기화 시 null 들어가 DOM도 비워지도록 제어형 바인딩
              value={
                filter.draftFilter.baseDateFrom && filter.draftFilter.baseDateTo
                  ? [
                      dayjs(filter.draftFilter.baseDateFrom),
                      dayjs(filter.draftFilter.baseDateTo),
                    ]
                  : null
              }
              onChange={(dates) =>
                filter.setDraftFilter({
                  ...filter.draftFilter,
                  baseDateFrom: dates?.[0]?.format('YYYY-MM-DD'),
                  baseDateTo: dates?.[1]?.format('YYYY-MM-DD'),
                })
              }
            />
          }
        />
        <BaseForm
          label="브랜드 ID"
          content={
            <InputNumber<number>
              style={{ width: 200 }}
              value={filter.draftFilter.storeId ?? null}
              onChange={(v) =>
                filter.setDraftFilter({
                  ...filter.draftFilter,
                  storeId: v ?? undefined,
                })
              }
              onPressEnter={handleSearch}
            />
          }
        />
        <BaseForm
          label="브랜드명"
          content={
            <Input
              style={{ width: 240 }}
              value={filter.draftFilter.brandName ?? ''}
              onChange={(e) =>
                filter.setDraftFilter({
                  ...filter.draftFilter,
                  brandName: e.target.value || undefined,
                })
              }
              onPressEnter={handleSearch}
            />
          }
        />
        <BaseForm
          label="정산 주기 타입"
          content={
            <Select
              mode="multiple"
              style={{ minWidth: 240 }}
              value={filter.draftFilter.periodTypes}
              onChange={(v) =>
                filter.setDraftFilter({ ...filter.draftFilter, periodTypes: v })
              }
              options={(
                Object.keys(SETTLEMENT_ITEM_PERIOD_TYPE_LABEL) as Array<
                  keyof typeof SETTLEMENT_ITEM_PERIOD_TYPE_LABEL
                >
              ).map((k) => ({
                value: k,
                label: SETTLEMENT_ITEM_PERIOD_TYPE_LABEL[k],
              }))}
            />
          }
        />
        <BaseForm
          label="정산 상태"
          content={
            <Select
              mode="multiple"
              style={{ minWidth: 240 }}
              value={filter.draftFilter.statuses}
              onChange={(v) =>
                filter.setDraftFilter({ ...filter.draftFilter, statuses: v })
              }
              // 정산 완료는 변경 대상 자체가 아니라 옵션에서도 제외
              options={(['ready', 'hold'] as const).map((k) => ({
                value: k,
                label: SETTLEMENT_ITEM_STATUS_LABEL[k],
              }))}
            />
          }
        />
        <BaseForm
          label="이력 있는 항목만"
          content={
            <StyledHistoryOnlySwitch>
              <Switch
                checked={!!filter.draftFilter.hasHistoryOnly}
                onChange={(checked) =>
                  filter.setDraftFilter({
                    ...filter.draftFilter,
                    hasHistoryOnly: checked || undefined,
                  })
                }
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </StyledHistoryOnlySwitch>
          }
        />
      </SearchBox>

      {/* ─── 변경 조건 설정 카드 ────────────────── */}
      <StyledActionCard>
        <p className="section-title">변경 조건 설정</p>
        <ShiftForm
          shiftMode={shiftConfig.shiftMode}
          deltaDays={shiftConfig.deltaDays}
          targetBaseDate={shiftConfig.targetBaseDate}
          reason={shiftConfig.reason}
          onShiftModeChange={shiftConfig.setShiftMode}
          onDeltaDaysChange={shiftConfig.setDeltaDays}
          onTargetBaseDateChange={shiftConfig.setTargetBaseDate}
          onReasonChange={shiftConfig.setReason}
        />
      </StyledActionCard>

      {/* ─── 툴바 (선택 건수 + 액션 버튼) ───────── */}
      <StyledToolbar>
        <StyledCountText>
          선택 <span>{selectedIds.length}</span>건 / 조회 <span>{totalCount}</span>건
        </StyledCountText>
        <StyledButtonGroup>
          <Button
            text="엑셀 업로드"
            color="light"
            onClick={() => setExcelModalOpen(true)}
          />
          <Button
            text="일괄 변경"
            color="primary"
            // 클릭 이벤트가 idsOverride로 흘러들어가지 않도록 wrap
            onClick={() => handlePreview()}
            disabled={previewMutation.isLoading}
          />
        </StyledButtonGroup>
      </StyledToolbar>

      {/* ─── 대상 정산건 테이블 ──────────────────── */}
      <Table<EligibleItem>
        className="eligible-items-table"
        rowKey="id"
        size="small"
        // isApplied=false (검색 전) 일 때는 useQuery 가 비활성화되어 isLoading 도 false 라
        // loading prop 자체가 그대로 false 가 됨. 그래서 별도 분기 불필요.
        loading={eligibleQuery.isLoading}
        dataSource={items}
        columns={columns}
        // 검색 전 / 검색 후 결과 0 건 두 케이스를 안내 메시지로 구분.
        locale={{
          emptyText: filter.isApplied
            ? '조회된 정산건이 없습니다.'
            : '검색 조건을 입력하고 [검색] 버튼을 눌러주세요.',
        }}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: (keys) => setSelectedIds(keys as number[]),
        }}
        pagination={{
          current: filter.appliedFilter.page ?? 1,
          pageSize: filter.appliedFilter.pageSize ?? 50,
          total: totalCount,
          showSizeChanger: true,
          onChange: (page, pageSize) =>
            filter.setAppliedFilter({ ...filter.appliedFilter, page, pageSize }),
        }}
        scroll={{ x: 'max-content' }}
      />

      {/* ─── Preview Modal ───────────────────────── */}
      <PreviewModal
        open={!!previewResult}
        result={previewResult}
        overrideMap={overrideMap}
        onOverrideChange={setOverride}
        onCancel={() => {
          setPreviewResult(null);
          resetOverrides();
        }}
        onConfirm={handleExecute}
        executing={executeMutation.isLoading}
      />

      {/* ─── 엑셀 업로드 Modal ───────────────────── */}
      <ExcelUploadModal
        isOpen={excelModalOpen}
        onCancel={() => setExcelModalOpen(false)}
        onSubmit={handleExcelUploadSubmit}
      />

      {/* ─── 정산번호 클릭 시 변경 이력 Modal ──── */}
      <ItemHistoryModal
        settlementItemId={historyItemId}
        onClose={() => setHistoryItemId(null)}
      />
    </StyledContainer>
  );
};
